import { useContext, useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { SmartContext } from '../../../library/Core/SmartContext';
import SessionContext from '../../../library/Core/SessionContext';
import { getDomainValueForCode } from '../../../library/Core/SmartFunctions';
import BusinessDirectoryService from './BusinessDirectoryService';
import useSmartState from '../../../common/hooks/useSmartState';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import AddServiceProviderModal from './AddServiceProviderModal';
import ComposeEmailModal from './ComposeEmailModal';
import InitialsAvatar, { hasProfilePicture } from '../../../library/SimpleControls/InitialsAvatar';

function AlumniDetailsModal({ providerId, className, modalTitle }) {
    const { state } = useContext(SmartContext);
    const { sessionState } = useContext(SessionContext);
    const { setStateValue } = useSmartState();
    const isAlumni = sessionState?.userTypeCode === 'ALUMNI';
    const isSuperAdmin = sessionState?.role === 'SUPER_ADMIN' || sessionState?.userTypeCode === 'SUPER_ADMIN';
    const canSendEmail = !isSuperAdmin;

    const [show, setShow] = useState(false);
    const [detail, setDetail] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showActions, setShowActions] = useState(false);
    const [editData, setEditData] = useState(null);

    const handleShow = () => {
        setShow(true);
        if (providerId && !isSuperAdmin) {
            BusinessDirectoryService.trackProviderView(providerId).catch((error) => {
                console.error('Error tracking provider view:', error);
            });
        }
        fetchDetail();
    };

    const handleClose = () => {
        setShow(false);
        setDetail(null);
        setShowActions(false);
    };

    const fetchDetail = async () => {
        if (!providerId) return;
        setIsLoading(true);
        try {
            const response = await BusinessDirectoryService.getProviderDetail(providerId);
            setDetail(response?.data || null);
        } catch (err) {
            console.error('Error fetching provider detail:', err);
            toast.error('Failed to load provider details');
        } finally {
            setIsLoading(false);
        }
    };

    const refreshList = () => {
        BusinessDirectoryService.search(state.data?.searchCriteria || {}).then((response) => {
            const payload = response?.data ?? {};
            setStateValue('providerList', payload?.data ?? []);
            setStateValue('pagination', {
                total: payload?.total ?? 0,
                page: payload?.page ?? 1,
                limit: payload?.limit ?? 20,
            });
        });
    };

    const handleRequestQuote = () => {
        if (detail?.email) {
            const name = detail?.providerName || '';
            window.location.href = `mailto:${detail.email}?subject=Request for Quote&body=Dear ${name},%0D%0A%0D%0AI would like to request a quote for your services.%0D%0A%0D%0AThank you.`;
        } else {
            toast.info('Email address not available');
        }
    };

    const handleViewProfile = () => {
        if (detail?.userId) {
            window.open(`/alumni/${detail.userId}/profile`, '_blank');
        } else {
            toast.info('Profile not available');
        }
    };

    const handleMarkInactive = async () => {
        const confirmation = await Swal.fire({
            icon: 'warning',
            title: 'Mark as Inactive?',
            text: 'Are you sure you want to mark this provider as inactive? They will not appear in search results but data will be preserved.',
            showCancelButton: true,
            confirmButtonText: 'Mark Inactive',
            confirmButtonColor: '#ea580c',
        });
        if (!confirmation.isConfirmed) return;

        try {
            await BusinessDirectoryService.markInactive(providerId);
            toast.success('Provider marked as inactive');
            handleClose();
            refreshList();
        } catch (err) {
            toast.error('Failed to mark provider as inactive');
        }
    };

    const handleRemove = async () => {
        const confirmation = await Swal.fire({
            icon: 'warning',
            title: 'Remove from Directory?',
            html: '<strong>WARNING:</strong> This will permanently remove this provider from the directory.',
            input: 'text',
            inputPlaceholder: 'Type DELETE to confirm',
            showCancelButton: true,
            confirmButtonText: 'Remove',
            confirmButtonColor: '#dc2626',
            inputValidator: (value) => {
                if (value !== 'DELETE') return 'Please type DELETE to confirm';
            },
        });
        if (!confirmation.isConfirmed) return;

        try {
            await BusinessDirectoryService.removeProvider(providerId);
            toast.success('Provider removed from directory');
            handleClose();
            refreshList();
        } catch (err) {
            toast.error('Failed to remove provider');
        }
    };

    const getAvailabilityBadge = (status) => {
        switch (status?.toUpperCase()) {
            case 'AVAILABLE':
                return <span className="badge rounded-pill bg-success-subtle bg-opacity-10 text-success font-14 font-500 px-3 py-2">Available</span>;
            case 'BUSY':
                return <span className="badge rounded-pill bg-warning-subtle bg-opacity-10 text-warning font-14 font-500 px-3 py-2">Busy</span>;
            case 'NOT_AVAILABLE':
                return <span className="badge rounded-pill bg-danger-subtle bg-opacity-10 text-danger font-14 font-500 px-3 py-2">Not Available</span>;
            default:
                return <span className="badge rounded-pill bg-secondary-subtle bg-opacity-10 text-secondary font-14 font-500 px-3 py-2">{status || 'Unknown'}</span>;
        }
    };

    const composeEmailProvider = detail
        ? {
            providerId: detail.providerId || providerId,
            alumniName: detail.providerName || '',
            contactEmailId: detail.email || '',
            category: getDomainValueForCode(detail.categoryCode, 'BUSINESS_CATEGORY', state) || detail.categoryCode || '',
            subcategory: getDomainValueForCode(detail.subcategoryCode, 'BUSINESS_SUBCATEGORY', state) || detail.subcategoryCode || '',
        }
        : null;

    return (
        <>
            <a href="javascript:;" className={className} onClick={handleShow}><span>{modalTitle}</span></a>

            <Modal show={show} onHide={handleClose} size="lg max-600" centered>
                <Modal.Body className="p-0 radius-15 overflow-hidden">

                    {isLoading && (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    )}

                    {!isLoading && detail && (
                        <>
                            {/* Header */}
                            <div className="bg-gray-f5 p-4 p-md-5">
                                <a href="javascript:;" className="position-absolute top-20 right-20 m-0 text-secondary font-30" onClick={handleClose} style={{ cursor: 'pointer' }}>
                                    <i className="bi bi-x-circle-fill"></i>
                                </a>
                                <div className="row g-4 align-items-center mb-4 mb-md-0">
                                    <div className="col-sm-auto">
                                        <div className="alumniDetailPic position-relative d-flex justify-content-center align-items-center">
                                            <i className="bi bi-check-circle-fill font-25 me-1 text-green position-absolute" style={{ top: '-6px', right: '-6px' }}></i>
                                            {hasProfilePicture(detail.imagePath) ? (
                                                <img src={detail.imagePath} className="rounded-circle" width="80" height="80" alt={detail.providerName} style={{ objectFit: 'cover' }} />
                                            ) : (
                                                <InitialsAvatar name={detail.providerName} size={80} />
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-sm">
                                        <h3 className="font-30 font-600 mb-0">{detail.providerName || 'N/A'}</h3>
                                        <p className="font-20 font-400 mb-0">{[detail.programName, detail.passOutYear].filter(Boolean).join(', ')}</p>
                                        {/* Action Buttons */}
                                <div className="acion_btn_section mt-3">
                                    <div className="row g-2 align-items-center ">
                                        <div className="col-auto">
                                            {canSendEmail && <ComposeEmailModal btnTitle="Send Email" className="btn-green height-35 px-3" provider={composeEmailProvider} iconMain={undefined} />}
                                        </div>
                                        {/* <div className="col-auto">
                                            <button className="btn-darkblue height-35 px-3" onClick={handleRequestQuote}>
                                                <i className="bi bi-journal-check me-2 position-relative"></i>
                                                <span className="font-16 font-400">Request Quote</span>
                                            </button>
                                        </div> */}
                                        <div className="col-auto">
                                            <button className="btn-border-gray height-35 px-3" onClick={handleViewProfile}>
                                                <i className="icon-user me-2 position-relative"></i>
                                                <span className="font-16 font-400">View Profile</span>
                                            </button>
                                        </div>
                                        {/* {!isAlumni && (
                                            <div className="col-auto">
                                                <div className="position-relative">
                                                    <a className="text-green font-30" href="javascript:;" onClick={() => setShowActions(!showActions)}>
                                                        <i className="bi bi-gear-fill"></i>
                                                    </a>
                                                    {showActions && (
                                                        <div className="position-absolute top-100 end-0 max-210" style={{ zIndex: 1060 }}>
                                                            <div className="card card-body border-0 shadow-sm p-3">
                                                                <a href="javascript:;" className="font-14 text-dark-blue text-decoration-none mb-2" onClick={() => { setShowActions(false); setShow(false); setEditData(detail); }}>
                                                                    <i className="bi bi-pencil-square me-1"></i> Edit Details
                                                                </a>
                                                                <a href="javascript:;" className="font-14 text-dark-blue text-decoration-none mb-2" onClick={() => { setShowActions(false); handleMarkInactive(); }}>
                                                                    <i className="bi bi-person-fill-x me-1"></i> Mark as Inactive
                                                                </a>
                                                                <a href="javascript:;" className="font-14 text-dark-blue text-decoration-none" onClick={() => { setShowActions(false); handleRemove(); }}>
                                                                    <i className="bi bi-trash me-1"></i> Remove from Directory
                                                                </a>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )} */}
                                    </div>
                                </div>
                                    </div>
                                </div>

                                
                            </div>

                            {/* Body */}
                            <div className="p-4 p-md-5">
                                <div className="row g-4">

                                    {/* Service Information */}
                                    <div className="col-md-12">
                                        <label className="form-label font-20 mb-1">Service Information</label>
                                        <div className="bg-gray-f5 px-4 py-3 rounded">
                                            <div className="row g-3 g-2 align-items-center justify-content-between">
                                                <div className="col-md-6 col-sm-6">
                                                    <span className="font-16 font-500">Service Category</span>
                                                    <p className="font-16 mb-0">
                                                        {getDomainValueForCode(detail.categoryCode, 'BUSINESS_CATEGORY', state) || detail.categoryCode || 'N/A'}
                                                    </p>
                                                </div>
                                                <div className="col-md-6 col-sm-6">
                                                    <span className="font-16 font-500">Specialization</span>
                                                    <p className="font-16 mb-0">
                                                        {getDomainValueForCode(detail.subcategoryCode, 'BUSINESS_SUBCATEGORY', state) || detail.subcategoryCode || 'N/A'}
                                                    </p>
                                                </div>
                                                <div className="col-md-6 col-sm-6">
                                                    <span className="font-16 font-500">Business Name</span>
                                                    <p className="font-16 mb-0">{detail.businessName || 'N/A'}</p>
                                                </div>
                                                <div className="col-md-6 col-sm-6">
                                                    <span className="font-16 font-500">Experience</span>
                                                    <p className="font-16 mb-0">{detail.experienceYears ? `${detail.experienceYears} Years` : 'N/A'}</p>
                                                </div>
                                                <div className="col-md-6 col-sm-6">
                                                    <span className="font-16 font-500">Location</span>
                                                    <p className="font-16 mb-0">
                                                        {[
                                                            detail.city && (getDomainValueForCode(detail.city, 'CITY_CODE', state) ?? ''),
                                                            detail.country && (getDomainValueForCode(detail.country, 'COUNTRY_CODE', state) ?? '')
                                                        ].filter(Boolean).join(', ') || 'N/A'}
                                                    </p>
                                                </div>
                                                <div className="col-md-6 col-sm-6">
                                                    <div className="d-flex flex-column align-items-start">
                                                        <span className="font-16 font-500">Availability</span>
                                                        {getAvailabilityBadge(detail.availabilityStatus)}
                                                    </div>
                                                </div>
                                                {detail.pricing && (
                                                    <div className="col-md-6 col-sm-6">
                                                        <span className="font-16 font-500">Pricing</span>
                                                        <p className="font-16 mb-0">{detail.pricing}</p>
                                                    </div>
                                                )}
                                                {detail.servicesDescription && (
                                                    <div className="col-12">
                                                        <span className="font-16 font-500">Services Description</span>
                                                        <p className="font-16 mb-0" style={{ whiteSpace: 'break-spaces' }}>{detail.servicesDescription}</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Contact */}
                                    <div className="col-md-12">
                                        <label className="form-label font-20 mb-1">Contact</label>
                                        <div className="bg-gray-f5 px-4 py-3 rounded">
                                            {detail.phone && (
                                                <p className="font-16 mb-2">
                                                    <i className="bi bi-telephone-fill text-dark-blue"></i>{' '}
                                                    <a className="text-dark-blue text-decoration-none" href={`tel:${detail.phone}`}>{detail.phone}</a>
                                                </p>
                                            )}
                                            {detail.email && (
                                                <p className="font-16 mb-2">
                                                    <i className="bi bi-envelope-fill text-dark-blue"></i>{' '}
                                                    <a className="text-dark-blue text-decoration-none" href={`mailto:${detail.email}`}>{detail.email}</a>
                                                </p>
                                            )}
                                            {detail.websiteUrl && (
                                                <p className="font-16 mb-0">
                                                    <i className="bi bi-globe text-dark-blue"></i>{' '}
                                                    <a className="text-dark-blue text-decoration-none" href={detail.websiteUrl.startsWith('http') ? detail.websiteUrl : `https://${detail.websiteUrl}`} target="_blank" rel="noopener noreferrer">
                                                        {detail.websiteUrl}
                                                    </a>
                                                </p>
                                            )}
                                            {!detail.phone && !detail.email && !detail.websiteUrl && (
                                                <p className="font-16 mb-0 text-muted">No contact information available.</p>
                                            )}
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </>
                    )}

                    {!isLoading && !detail && show && (
                        <div className="text-center py-5 text-muted">
                            <p>Unable to load provider details.</p>
                        </div>
                    )}

                </Modal.Body>
            </Modal>

            {/* Edit modal — opens when Edit Details is clicked */}
            {editData && (
                <AddServiceProviderModal
                    editData={editData}
                    onEditClose={() => {
                        setEditData(null);
                        refreshList();
                    }}
                />
            )}
        </>
    );
}

export default AlumniDetailsModal;
